import { useSnackbar } from 'notistack';
import { captureException } from '@sentry/react';
import { removeTypename } from 'util/helpers';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { agaveService } from 'app/services/agave.service';
import {
  markErpTransactionReviewed,
  updateTransaction,
  setTransactionPosting,
  applyLocalTransactionUpdates,
  refetchTransaction,
  selectTransactionsSlice,
  selectAllTransactions,
} from 'app/pages/store/transactionsSlice';
import store, { useAppDispatch, useAppSelector } from 'app/store';
import { useAgaveSourceSystem } from 'app/hooks/useAgaveSourceSystem';
import { type ErpPostingAccount } from 'app/services/erp-connection.service';
import { selectCardsDataSlice } from 'app/pages/store/CardDataSlice/cardDataSlice';
import {
  createAgaveAPInvoice,
  createOrUpdateAgaveExpense,
  createSageIntacctVendor,
  fetchProjectAutoFillDimensions,
  getGlCodesByCosting,
  nextAgaveCodingSeq,
  selectAgaveSlice,
  updateTransactionAgaveClient,
  createCreditCardCreditQBO,
} from 'app/pages/store/agaveSlice';
import {
  AgaveCostCode,
  AgaveCostType,
  AgaveError,
  AgaveGlCode,
  AgaveGlCostType,
  AgaveProject,
  AgaveSubJob,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveSubaccount,
  AgaveEmployeeEntity,
  AgaveHttpMethod,
  CreateAgaveVendorInput,
  AgaveAPInvoicesCreateExpenseInput,
  CreateAgaveExpenseInput,
  CreateAgaveCostCodeInput,
  CreateAgaveCostTypeInput,
  CreateAgaveGlCodeInput,
  CreateAgaveProjectInput,
  ERPPostingStatus,
  FlexCostType,
  HighnoteTransactionEvent,
} from 'API';
import { useTranslation } from 'react-i18next';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { AgaveSourceSystem, SourceSystemsWithGLCodes } from 'app/services/agave/agaveConstants';
import {
  formatAmountForAgavePost,
  isAgaveTransactionAllowedToCode,
  isBillablePostingEnabled,
  isUnconfirmedPostingError,
} from 'app/services/agave/erpUtils';
import {
  qboUtil,
  postSage300APInvoice,
  postSage300JobCost,
  postQBOTransaction,
  postQBDTransaction,
  postSageIntacctTransaction,
  postVistaTransaction,
  postAcumaticaTransaction,
  type PostingContext,
} from 'app/services/agave/erp';

/**
 * Maps technical Agave/ERP error messages to user-friendly messages
 * @param message - The raw error message from Agave API or ERP system
 * @param t - The translation function from react-i18next
 * @returns A user-friendly error message
 */
export function mapAgaveErrorMessage(message: string, t: (key: string) => string): string {
  // SEE-1259: an unconfirmed outcome is not a rejection — never show it as one,
  // and never leak the marker. Must precede every other branch.
  if (isUnconfirmedPostingError(message)) {
    return t('submitExpense.postingUnconfirmed');
  }

  // Handle Sage Intacct missing account number / GL code errors
  if (
    message.includes('Enter an account number') ||
    (message.includes('account number') && message.includes('BL03002110'))
  ) {
    return t('sageIntacct.missingGLCode');
  }

  // Handle cost type validation errors
  const costTypeMatch = message.match(/Cost type ['"]([^'"]+)['"] specified is not valid/i);
  if (costTypeMatch) {
    return t('sageIntacct.invalidCostType');
  }

  // Handle cost code validation errors
  const costCodeMatch = message.match(/Cost code ['"]([^'"]+)['"] specified is not valid/i);
  if (costCodeMatch) {
    return t('sageIntacct.invalidCostCode');
  }

  // Handle generic "not valid" errors with Sage Intacct error codes
  if (message.includes('is not valid') || message.includes('BL01001973')) {
    return t('sageIntacct.invalidCodingCombination');
  }

  // Handle QuickBooks Desktop native errors
  if (message.includes('[QuickBooks Desktop Error]')) {
    return t('submitExpense.qbdError');
  }

  // Handle QBD/Agave disconnection errors
  if (
    (message.toLowerCase().includes('quickbooks desktop') &&
      message.toLowerCase().includes('disconnected')) ||
    message.includes('Failed to retrieve bank accounts from QuickBooks Desktop')
  ) {
    return t('submitExpense.qbdDisconnected');
  }

  // Handle "No bank account found" error from getBankAccountId
  if (message.includes('No bank account found with name')) {
    return t('submitExpense.qbdNoBankAccount');
  }

  // Handle JS crash when posting fails due to missing vendor. Map both explicit "reading 'vendor'"
  // and "reading '0'" (common when vendor/data is undefined and code does data[0]).
  if (message.includes('Cannot read properties of undefined')) {
    const isVendorRelated =
      message.toLowerCase().includes("reading 'vendor'") ||
      message.toLowerCase().includes('reading "vendor"') ||
      message.includes("reading '0'");
    if (isVendorRelated) {
      return t('submitExpense.vendorMustBeCreatedInErp');
    }
    // Catch-all for other undefined-property crashes (e.g. reading 'find' from getBankAccountId)
    return t('submitExpense.unexpectedError');
  }

  // Handle both underscore and space formats
  const hasItemId = message.includes('item_id') || message.includes('item id');
  const hasLedgerAccountId = message.includes('ledger_account_id') || message.includes('ledger account id');

  if (hasItemId && hasLedgerAccountId) {
    if (message.includes('prohibits')) {
      return t('sageIntacct.glAndCostCodeProhibited');
    }
    if (message.includes('required when') || message.includes('is not present')) {
      return t('sageIntacct.glOrCostCodeRequired');
    }
  }

  // Handle vendor must be created in ERP (e.g. CMiC, Acumatica)
  const lowerMessage = message.toLowerCase();
  if (
    lowerMessage.includes('vendor') &&
    (lowerMessage.includes('created manually') || lowerMessage.includes('cmic'))
  ) {
    return t('submitExpense.vendorMustBeCreatedInErp');
  }

  // SEE-1259: last resort — Agave prefixes the source system's own text (e.g.
  // "[Sage 300 CRE Error] [0] <reason>"). Strip the bracketed noise and keep the
  // reason, which is the actionable part on a genuine rejection.
  //
  // Scoped to Sage 300 deliberately (review finding, PR #2571). Two reasons: the
  // other Agave ERPs are out of scope for this ticket and their messages must not
  // change, and a neutral "System: reason" label is all we can honestly show —
  // asserting a *rejection* would claim an outcome we don't have for a 5xx.
  //
  // MUST stay below every specific branch above: those match on the reason text,
  // and rewriting the prefix first would stop them ever being reached.
  const sagePrefixed = message.match(/^\s*\[Sage 300 CRE Error\]\s*(?:\[\d+\]\s*)?(.*)$/s);
  const sageReason = sagePrefixed?.[1]?.trim();
  if (sageReason) {
    return `Sage 300 CRE: ${sageReason}`;
  }

  return message;
}

export type AgaveClientProps = {
  transaction?: HighnoteTransactionEvent;
};

/** Optional: when provided, clearing the employee dropdown registers the txn so auto-fill won't re-apply. */
export const AgaveEmployeeClearedContext = createContext<((transactionId: string) => void) | null>(null);

export const useAgaveClientUtils = ({ transaction }: AgaveClientProps = {}) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const registerEmployeeCleared = useContext(AgaveEmployeeClearedContext);

  const { id } = transaction || {};
  const { agaveAccount, accountToken: agaveAccountToken, vendors } = useAppSelector(selectAgaveSlice);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { cardDetails } = useAppSelector(selectCardsDataSlice);
  const { postingTransactionIds } = useAppSelector(selectTransactionsSlice);
  const [selectedVendor, setSelectedVendorId] = useState<Record<string, AgaveVendor>>({});
  const [isAgaveLoading, setIsAgaveLoading] = useState(false);

  const { sourceSystemIn: agaveAccountInSourceSystem, currentSourceSystem } = useAgaveSourceSystem();

  // Note: We intentionally do NOT cleanup subscriptions on unmount because:
  // 1. Subscriptions already unsubscribe themselves on COMPLETED/FAILED (lines 701, 727, 991, 1017)
  // 2. If user navigates away during posting, we want the subscription to continue so they receive
  //    updates when they return (subscription handlers update Redux state which persists)
  // 3. Cleaning up on unmount breaks this functionality - subscriptions are lost and status gets stuck
  // 4. The only potential memory leak is if a subscription never completes (edge case - network failure)
  //    In practice, subscriptions complete within seconds, so this is an acceptable trade-off

  const extractFullNameFromSourceData = useCallback(
    (code: AgaveGlCode | AgaveCostCode | AgaveCostType | AgaveGlCostType | FlexCostType) =>
      code && 'source_data' in code ? JSON.parse(code.source_data?.data ?? '{}') : { FullName: '' },
    []
  );

  const extractFullyQualifiedNameFromSourceData = useCallback(
    (code: AgaveGlCode | AgaveCostCode | AgaveCostType | AgaveGlCostType | FlexCostType) =>
      code && 'source_data' in code ? JSON.parse(code.source_data?.data ?? '{}') : { FullyQualifiedName: '' },
    []
  );

  const isQuickbooksAgaveClient = useMemo(
    () =>
      agaveAccountInSourceSystem(AgaveSourceSystem.QUICKBOOKS_ONLINE, AgaveSourceSystem.QUICKBOOKS_DESKTOP),
    [agaveAccountInSourceSystem]
  );

  const isERPSupportingGLCodes = useMemo(() => {
    return agaveAccountInSourceSystem(...SourceSystemsWithGLCodes);
  }, [agaveAccountInSourceSystem]);

  const handleChangeGlCodes = (selectedGlCode: AgaveGlCode | null) => {
    if (!id) return;

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[Agave] change GL', { id, glId: selectedGlCode?.id });
    }

    dispatch(
      applyLocalTransactionUpdates([
        {
          id,
          changes: {
            agaveGlCode: selectedGlCode || null,
            agaveGlCodeId: selectedGlCode?.id || null,
          },
        },
      ])
    );

    dispatch(
      updateTransactionAgaveClient({
        id,
        agaveGlCode: selectedGlCode?.id ? (removeTypename(selectedGlCode) as CreateAgaveGlCodeInput) : null,
      })
    );
  };

  const handleChangeProjects = useCallback(
    (selectedProject: AgaveProject | null) => {
      if (!id) return;

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[Agave] change Project', { id, projectId: selectedProject?.id });
      }

      const newProjectId = selectedProject?.id;

      // Get the ABSOLUTE latest transaction from Redux store
      const currentState = store.getState();
      const allTransactionsNow = selectAllTransactions(currentState);
      const latestTransaction = allTransactionsNow.find((t) => t.id === id);
      const currentProject = latestTransaction?.agaveProject;
      const currentSubJob = latestTransaction?.agaveSubJob;
      const currentCostCode = latestTransaction?.agaveCostCode;
      const currentCostType = latestTransaction?.agaveCostType;
      const currentLocation = latestTransaction?.agaveLocation;
      const currentDepartment = latestTransaction?.agaveDepartment;

      const currentProjectId = currentProject?.id;
      const projectIsChanging = currentProjectId !== newProjectId;

      // SEE-974: Project and Equipment are mutually exclusive. Selecting a project
      // clears any equipment-group coding so a row never holds both — enforced here
      // at coding time, independent of the selected Transaction type (e.g. on a
      // GL/untyped row where neither group is greyed). It does NOT change the
      // transaction type: the type is the user's explicit choice (only the Type
      // dropdown changes it). Issued as its own write (disjoint fields).
      const hasEquipmentCoding =
        !!latestTransaction?.agaveEquipmentId ||
        !!latestTransaction?.agaveEmCostCode ||
        !!latestTransaction?.agaveEmCostType;
      if (newProjectId && hasEquipmentCoding) {
        dispatch(
          applyLocalTransactionUpdates([
            {
              id,
              changes: {
                agaveEquipment: null,
                agaveEquipmentId: null,
                agaveEmCostCode: null,
                agaveEmCostCodeId: null,
                agaveEmCostType: null,
                agaveEmCostTypeId: null,
              },
            },
          ])
        );
        dispatch(
          updateTransactionAgaveClient({
            id,
            agaveEquipment: null,
            agaveEmCostCode: null,
            agaveEmCostType: null,
            clientWriteSeq: nextAgaveCodingSeq(),
          })
        );
      }

      // Check if this is Sage Intacct (has different dependency rules)
      const isSageIntacct = agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_INTACCT);

      // SEE-1112: dimension-only Intacct paidols (no Projects module) code
      // against the Class/Item dimensions, which have NO project relationship —
      // so project changes must not cascade-clear cost code/type for them.
      // Location/department stay project-associated (projects still carry those
      // dimensions), so their clearing/auto-fill behavior is unchanged.
      const intacctDimensionCoding = isSageIntacct && !!selectedPaidol?.intacctProjectsNotLicensed;

      let needsToClearSubJob = false;
      let needsToClearCostCode = false;
      let needsToClearCostType = false;
      let needsToClearLocation = false;
      let needsToClearDepartment = false;

      // SEE-808 / SEE-802: Sage Intacct only — after the project change is applied,
      // look up the project's associated Department and Location dimensions and
      // auto-pick when there is exactly one unique value per dimension. Overwrites
      // location/department when the auto-fill returns a value that differs from
      // the row's current value so a second project change refreshes them (the
      // row keeps the previous project's dimensions until this resolves; without
      // the "differs" check, the second change would never replace them). The
      // project-id check guards against rapid project switches landing the
      // previous project's dimensions on the new one.
      //
      // SEE-944 flicker fix — this function is also responsible for issuing the
      // clear of location/department when the auto-fill returns no dimensions.
      // The caller skips the upfront clear (both local and backend) so the user
      // sees a single transition (old → new or old → cleared) instead of two
      // clear/fill cycles caused by a race between the project-change backend
      // response and the auto-fill response.
      //
      // `shouldClearLocationDeptOnNoDimensions` lets the caller request that we
      // null out location/department when the auto-fill returns empty — used
      // when those fields are project-dependent on this ERP and need to stay in
      // sync with the new project.
      const triggerProjectDimensionAutoFill = (shouldClearLocationDeptOnNoDimensions = false) => {
        const paidolId = latestTransaction?.paidolId || selectedPaidol?.id;
        if (!isSageIntacct || !newProjectId || !paidolId) return;
        dispatch(fetchProjectAutoFillDimensions({ paidolId, projectId: newProjectId }))
          .unwrap()
          .then(({ location, department }) => {
            const stateNow = store.getState();
            const txnNow = selectAllTransactions(stateNow).find((t) => t.id === id);
            if (!txnNow || txnNow.agaveProjectId !== newProjectId) return;

            const localChanges: Record<string, unknown> = {};
            const backendUpdates: {
              agaveLocation?: AgaveLocation | null;
              agaveDepartment?: AgaveDepartment | null;
            } = {};

            if (location && location.id !== txnNow.agaveLocationId) {
              const cleaned = removeTypename(location) as AgaveLocation;
              localChanges.agaveLocation = cleaned;
              localChanges.agaveLocationId = cleaned.id;
              backendUpdates.agaveLocation = cleaned;
            } else if (!location && shouldClearLocationDeptOnNoDimensions && txnNow.agaveLocationId) {
              localChanges.agaveLocation = null;
              localChanges.agaveLocationId = null;
              backendUpdates.agaveLocation = null;
            }
            if (department && department.id !== txnNow.agaveDepartmentId) {
              const cleaned = removeTypename(department) as AgaveDepartment;
              localChanges.agaveDepartment = cleaned;
              localChanges.agaveDepartmentId = cleaned.id;
              backendUpdates.agaveDepartment = cleaned;
            } else if (!department && shouldClearLocationDeptOnNoDimensions && txnNow.agaveDepartmentId) {
              localChanges.agaveDepartment = null;
              localChanges.agaveDepartmentId = null;
              backendUpdates.agaveDepartment = null;
            }

            if (Object.keys(localChanges).length === 0) return;
            dispatch(applyLocalTransactionUpdates([{ id, changes: localChanges }]));
            dispatch(
              updateTransactionAgaveClient({ id, ...backendUpdates, clientWriteSeq: nextAgaveCodingSeq() })
            );
          })
          .catch(() => {
            /* best-effort — failure leaves the fields as-is; user picks manually */
          });
      };

      // Check if we need to clear the subjob due to project change/removal
      if (currentSubJob) {
        const currentSubJobProjectId = currentSubJob.projectId;

        // If project is removed or changed to a different project
        if (!newProjectId || currentSubJobProjectId !== newProjectId) {
          needsToClearSubJob = true;
          const subJobName = currentSubJob.name || currentSubJob.number || 'selected sub-job';

          if (!newProjectId) {
            // Project removed
            enqueueSnackbar(
              t('transactionSidesheet.subJobRequiresProject', {
                subJob: subJobName,
                defaultValue: `${subJobName} cannot be used without a project`,
              }),
              {
                variant: 'info',
                autoHideDuration: 3000,
              }
            );
          } else {
            // Project changed to a different one
            const newProjectName = selectedProject.name || selectedProject.number || 'the new project';
            enqueueSnackbar(
              t('transactionSidesheet.subJobIncompatibleWithProject', {
                subJob: subJobName,
                project: newProjectName,
                defaultValue: `${subJobName} cannot be used with ${newProjectName}`,
              }),
              {
                variant: 'info',
                autoHideDuration: 3000,
              }
            );
          }
        }
      }

      // Check if we need to clear cost code due to project change or first project selection.
      // This covers two cases:
      // 1. Project changing from one to another — cost code may belong to the old project
      // 2. Project selected for the first time — cost code was picked without a project,
      //    so it may be tied to a different project (e.g. Sage 300 CRE creates per-project cost codes)
      if (currentCostCode && projectIsChanging && newProjectId) {
        const costCodeProjectId = currentCostCode.projectId;
        // If cost code belongs to a different project (or any project when none was selected), clear it
        if (costCodeProjectId && costCodeProjectId !== newProjectId) {
          needsToClearCostCode = true;
          const costCodeName = currentCostCode.name || currentCostCode.code || 'selected cost code';
          const newProjectName = selectedProject.name || selectedProject.number || 'the new project';

          enqueueSnackbar(
            t('transactionSidesheet.costCodeIncompatibleWithProject', {
              costCode: costCodeName,
              project: newProjectName,
              defaultValue: `${costCodeName} cannot be used with ${newProjectName}`,
            }),
            {
              variant: 'info',
              autoHideDuration: 3000,
            }
          );
        }
      }

      // If we're clearing cost code, also need to clear cost type
      if (needsToClearCostCode && currentCostType) {
        needsToClearCostType = true;
      }

      // Cost code and cost type validation is now handled server-side via join tables
      // The dropdowns only show valid options for the selected project
      // When project changes, clear subjob (if incompatible) and cost code/type (always, since project-specific)

      // For ERPs where cost codes are project-dependent (Sage 300 CRE, Sage Intacct),
      // clear cost code/type when project changes — including first selection.
      // This covers the case where a cost code was selected before any project;
      // it must be cleared because it's tied to a specific project.
      const isSage300CRE = agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_300_CRE);
      const costCodesAreProjectDependent = isSage300CRE || (isSageIntacct && !intacctDimensionCoding);
      const shouldClearCostCoding =
        projectIsChanging && newProjectId && (currentProjectId || costCodesAreProjectDependent);

      // If shouldClearCostCoding is true and we haven't already flagged the cost code for clearing
      if (shouldClearCostCoding && !needsToClearCostCode && currentCostCode) {
        needsToClearCostCode = true;
        const costCodeName = currentCostCode.name || currentCostCode.code || 'selected cost code';
        const newProjectName = selectedProject.name || selectedProject.number || 'the new project';

        enqueueSnackbar(
          t('transactionSidesheet.costCodeClearedForProjectChange', {
            costCode: costCodeName,
            project: newProjectName,
            defaultValue: `${costCodeName} cleared - cost codes are project-specific`,
          }),
          {
            variant: 'info',
            autoHideDuration: 3000,
          }
        );
      }

      if (shouldClearCostCoding && !needsToClearCostType && currentCostType) {
        needsToClearCostType = true;
      }

      // For Sage Intacct: Department, Location, CostCode, and CostType are all dependent on Project.
      // Clear them when: (1) switching from one project to another, or (2) selecting a project when
      // location/department are already set (e.g. user picked employee then location then project last).
      const shouldClearProjectDependents =
        isSageIntacct &&
        newProjectId &&
        ((projectIsChanging && currentProjectId) || !!currentLocation || !!currentDepartment);

      if (shouldClearProjectDependents) {
        // Clear CostCode if it exists and we haven't already flagged it
        // (SEE-1112: not for dimension-only paidols — Class codes are project-independent)
        if (!intacctDimensionCoding && !needsToClearCostCode && currentCostCode) {
          needsToClearCostCode = true;
          const costCodeName = currentCostCode.name || currentCostCode.code || 'selected cost code';
          enqueueSnackbar(
            t('transactionSidesheet.costCodeClearedForProjectChange', {
              costCode: costCodeName,
              project: selectedProject?.name || selectedProject?.number || 'the new project',
              defaultValue: `${costCodeName} cleared - cost codes are project-specific`,
            }),
            {
              variant: 'info',
              autoHideDuration: 3000,
            }
          );
        }

        // Clear CostType if it exists and we haven't already flagged it
        // (SEE-1112: not for dimension-only paidols — Item types are project-independent)
        if (!intacctDimensionCoding && !needsToClearCostType && currentCostType) {
          needsToClearCostType = true;
        }

        // Clear Location if it exists
        if (currentLocation) {
          needsToClearLocation = true;
          const locationName = currentLocation.name || currentLocation.source_id || 'selected location';
          enqueueSnackbar(
            t('transactionSidesheet.locationClearedForProjectChange', {
              location: locationName,
              project: selectedProject?.name || selectedProject?.number || 'the new project',
              defaultValue: `${locationName} cleared - locations are project-specific`,
            }),
            {
              variant: 'info',
              autoHideDuration: 3000,
            }
          );
        }

        // Clear Department if it exists
        if (currentDepartment) {
          needsToClearDepartment = true;
          const departmentName = currentDepartment.name || currentDepartment.code || 'selected department';
          enqueueSnackbar(
            t('transactionSidesheet.departmentClearedForProjectChange', {
              department: departmentName,
              project: selectedProject?.name || selectedProject?.number || 'the new project',
              defaultValue: `${departmentName} cleared - departments are project-specific`,
            }),
            {
              variant: 'info',
              autoHideDuration: 3000,
            }
          );
        }
      }

      // Apply the update with necessary clears
      if (
        needsToClearSubJob ||
        needsToClearCostCode ||
        needsToClearCostType ||
        shouldClearCostCoding ||
        needsToClearLocation ||
        needsToClearDepartment
      ) {
        // CRITICAL: Update local state immediately for instant UI feedback.
        //
        // SEE-944 flicker fix — when location/department need clearing on a
        // project change, we DON'T clear them optimistically. Instead, the
        // auto-fill below is the sole writer for those two fields, so the user
        // sees a single transition (old → new or old → cleared) instead of two
        // clear/fill cycles caused by the project-change backend response
        // racing with the auto-fill response.
        dispatch(
          applyLocalTransactionUpdates([
            {
              id,
              changes: {
                agaveProject: selectedProject?.id ? (removeTypename(selectedProject) as any) : null,
                agaveProjectId: selectedProject?.id || null,
                agaveSubJob: needsToClearSubJob ? null : undefined,
                agaveSubJobId: needsToClearSubJob ? null : undefined,
                agaveCostCode: needsToClearCostCode || shouldClearCostCoding ? null : undefined,
                agaveCostCodeId: needsToClearCostCode || shouldClearCostCoding ? null : undefined,
                agaveCostType: needsToClearCostType || shouldClearCostCoding ? null : undefined,
                agaveCostTypeId: needsToClearCostType || shouldClearCostCoding ? null : undefined,
                // agaveLocation / agaveDepartment intentionally omitted —
                // triggerProjectDimensionAutoFill below handles those.
              },
            },
          ])
        );
        // Then update backend (async). Same: location/department handled by
        // the auto-fill so they don't race with this response.
        dispatch(
          updateTransactionAgaveClient({
            id,
            agaveProject: selectedProject?.id
              ? (removeTypename(selectedProject) as CreateAgaveProjectInput)
              : null,
            agaveSubJob: needsToClearSubJob ? null : undefined,
            agaveCostCode: needsToClearCostCode || shouldClearCostCoding ? null : undefined,
            agaveCostType: needsToClearCostType || shouldClearCostCoding ? null : undefined,
            clientWriteSeq: nextAgaveCodingSeq(),
          })
        );
        // When the caller flagged location/department for clearing, the
        // auto-fill becomes responsible for either filling them with the new
        // project's dimensions OR explicitly clearing them if no dimensions
        // are mapped.
        triggerProjectDimensionAutoFill(needsToClearLocation || needsToClearDepartment);
        return;
      }

      // If project is being removed (null), apply ERP-specific clearing based on sync configs
      // See: amplify/backend/function/v2Agave/src/agave_api/erp/*.py for ERP configs
      if (!newProjectId) {
        const isSage300CRE = agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_300_CRE);
        const isQuickbooksDesktop = agaveAccountInSourceSystem(AgaveSourceSystem.QUICKBOOKS_DESKTOP);
        // SEE-975 / SEE-1656: Vista and Spectrum cost codes are project-scoped (via
        // the AgaveProjectCostCode join), so removing the project must clear cost
        // code + cost type too — matching the "change project" cascade and the
        // cost-code-removed cascade.
        const isViewpointVista = agaveAccountInSourceSystem(AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED);
        const isSpectrum = agaveAccountInSourceSystem(AgaveSourceSystem.SPECTRUM);

        // Cost codes are fetched per_project for Sage 300 CRE, Sage Intacct, Vista, and
        // Spectrum (SEE-1112: dimension-only Intacct paidols keep cost code/type —
        // Class/Item dimensions have no project relationship, so removing the project
        // must not wipe them)
        const costCodesFilteredByProject =
          isSage300CRE || (isSageIntacct && !intacctDimensionCoding) || isViewpointVista || isSpectrum;
        // SubJobs exist only for Sage 300 CRE and QuickBooks Desktop
        const hasSubJobs = isSage300CRE || isQuickbooksDesktop;
        // Cost types are filtered by project/cost code for the same ERPs
        const costTypesFilteredByProject = costCodesFilteredByProject;
        // Locations and departments are filtered by project for Sage Intacct only
        const locationsDepartmentsFilteredByProject = isSageIntacct;

        // Build changes object based on ERP-specific rules
        const localChanges: Record<string, any> = {
          agaveProject: null,
          agaveProjectId: null,
        };
        const backendUpdates: Record<string, any> = {
          agaveProject: null,
        };

        // SubJobs: Only Sage 300 CRE and QuickBooks Desktop have subJobs
        if (hasSubJobs && currentSubJob) {
          localChanges.agaveSubJob = null;
          localChanges.agaveSubJobId = null;
          backendUpdates.agaveSubJob = null;
        }

        // CostCodes: Only Sage 300 CRE and Sage Intacct filter by project
        if (costCodesFilteredByProject && currentCostCode) {
          localChanges.agaveCostCode = null;
          localChanges.agaveCostCodeId = null;
          backendUpdates.agaveCostCode = null;
        }

        // CostTypes: Only Sage 300 CRE and Sage Intacct filter by project
        if (costTypesFilteredByProject && currentCostType) {
          localChanges.agaveCostType = null;
          localChanges.agaveCostTypeId = null;
          backendUpdates.agaveCostType = null;
        }

        // Locations/Departments: Only Sage Intacct filters by project
        if (locationsDepartmentsFilteredByProject) {
          if (currentLocation) {
            localChanges.agaveLocation = null;
            localChanges.agaveLocationId = null;
            backendUpdates.agaveLocation = null;
          }
          if (currentDepartment) {
            localChanges.agaveDepartment = null;
            localChanges.agaveDepartmentId = null;
            backendUpdates.agaveDepartment = null;
          }
        }

        // CRITICAL: Update local state immediately for instant UI feedback
        dispatch(
          applyLocalTransactionUpdates([
            {
              id,
              changes: localChanges,
            },
          ])
        );
        // Then update backend (async)
        dispatch(
          updateTransactionAgaveClient({ id, ...backendUpdates, clientWriteSeq: nextAgaveCodingSeq() })
        );
        return;
      }

      // Normal project update (no subjob/costcode affected)
      // CRITICAL: Update local state immediately for instant UI feedback
      dispatch(
        applyLocalTransactionUpdates([
          {
            id,
            changes: {
              agaveProject: selectedProject?.id ? (removeTypename(selectedProject) as any) : null,
              agaveProjectId: selectedProject?.id || null,
            },
          },
        ])
      );

      // Then update backend (async)
      dispatch(
        updateTransactionAgaveClient({
          id,
          agaveProject: selectedProject?.id
            ? (removeTypename(selectedProject) as CreateAgaveProjectInput)
            : null,
          clientWriteSeq: nextAgaveCodingSeq(),
        })
      );
      triggerProjectDimensionAutoFill();
    },
    [
      id,
      dispatch,
      enqueueSnackbar,
      closeSnackbar,
      t,
      agaveAccountInSourceSystem,
      selectedPaidol?.id,
      selectedPaidol?.intacctProjectsNotLicensed,
    ]
  );

  const handleChangeCostCode = useCallback(
    (selectedCostCode: AgaveCostCode | null) => {
      if (!id) return;

      // Get the ABSOLUTE latest transaction from Redux store
      const currentState = store.getState();
      const allTransactionsNow = selectAllTransactions(currentState);
      const latestTransaction = allTransactionsNow.find((t) => t.id === id);
      const currentCostType = latestTransaction?.agaveCostType;
      const currentCostCode = latestTransaction?.agaveCostCode;

      // Both Sage 300 CRE and Sage Intacct have cost type dependent on cost code
      const isSage300CRE = agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_300_CRE);
      const isSageIntacct = agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_INTACCT);
      // SEE-975: the cascade-clear + write-seq flicker guard below are gated on
      // this so no other ERP's behavior changes. SEE-1656: Spectrum joins Vista —
      // its ingest now stamps cost_type_ids the same way.
      const isViewpointVista = agaveAccountInSourceSystem(AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED);
      const isSpectrum = agaveAccountInSourceSystem(AgaveSourceSystem.SPECTRUM);
      const hasCostCodeScopedCostTypes = isViewpointVista || isSpectrum;

      // For Sage Intacct: Cost Type is dependent on Cost Code
      // When cost code changes, always clear cost type
      const costCodeIsChanging = currentCostCode?.id !== selectedCostCode?.id;
      if (isSageIntacct && costCodeIsChanging && currentCostType) {
        const costTypeName = currentCostType.name || currentCostType.code || 'selected cost type';
        const costCodeName = selectedCostCode
          ? selectedCostCode.name || selectedCostCode.code || 'the new cost code'
          : 'none';

        enqueueSnackbar(
          t('transactionSidesheet.costTypeClearedForCostCodeChange', {
            costType: costTypeName,
            costCode: costCodeName,
            defaultValue: selectedCostCode
              ? `${costTypeName} cleared - cost types are cost code-specific`
              : `${costTypeName} cleared - cost types require a cost code`,
          }),
          {
            variant: 'info',
            autoHideDuration: 3000,
          }
        );

        // CRITICAL: Update local state immediately for instant UI feedback
        dispatch(
          applyLocalTransactionUpdates([
            {
              id,
              changes: {
                agaveCostCode: selectedCostCode || null,
                agaveCostCodeId: selectedCostCode?.id || null,
                agaveCostType: null,
                agaveCostTypeId: null,
              },
            },
          ])
        );

        // Then update backend asynchronously
        dispatch(
          updateTransactionAgaveClient({
            id,
            agaveCostCode: selectedCostCode?.id
              ? (removeTypename(selectedCostCode) as CreateAgaveCostCodeInput)
              : null,
            agaveCostType: null,
          })
        );
        return;
      }

      // Check if we need to clear the cost type due to cost code change (Sage 300 CRE specific validation)
      if (isSage300CRE && selectedCostCode && currentCostType) {
        // Use runtime check for cost_type_ids (exists in backend but not in TS types yet)
        const costCodeAny = selectedCostCode as any;
        const costCodeCostTypeIds = (costCodeAny.cost_type_ids as string[]) || [];
        const currentCostTypeId = currentCostType.id;

        // KEEP the cost type ONLY if it's explicitly in the cost code's valid list
        // Clear in all other cases:
        // 1. Cost code has no relationships (empty array) - can't validate, must clear
        // 2. Cost code has relationships but current cost type is not in the list - incompatible, must clear
        const shouldKeepCostType =
          costCodeCostTypeIds.length > 0 && costCodeCostTypeIds.includes(currentCostTypeId);

        if (!shouldKeepCostType) {
          // Show warning message
          const costTypeName = currentCostType.name || currentCostType.code || 'selected cost type';
          const costCodeName = selectedCostCode.name || selectedCostCode.code || 'selected cost code';

          enqueueSnackbar(
            t('transactionSidesheet.costTypeCostCodeIncompatible', {
              costType: costTypeName,
              costCode: costCodeName,
              defaultValue:
                costCodeCostTypeIds.length === 0
                  ? `${costTypeName} cleared - ${costCodeName} has no associated cost types`
                  : `${costTypeName} cannot be used with ${costCodeName}`,
            }),
            {
              variant: 'info',
              autoHideDuration: 3000,
            }
          );

          // CRITICAL: Update local state immediately for instant UI feedback
          dispatch(
            applyLocalTransactionUpdates([
              {
                id,
                changes: {
                  agaveCostCode: selectedCostCode || null,
                  agaveCostCodeId: selectedCostCode?.id || null,
                  agaveCostType: null,
                  agaveCostTypeId: null,
                },
              },
            ])
          );

          // Then update backend asynchronously
          dispatch(
            updateTransactionAgaveClient({
              id,
              agaveCostCode: selectedCostCode?.id
                ? (removeTypename(selectedCostCode) as CreateAgaveCostCodeInput)
                : null,
              agaveCostType: null,
            })
          );
          return;
        }
      }

      // SEE-975: Vista — cost types are cost-code-specific (via the
      // AgaveCostCodeCostType join, aggregated onto the cost code as
      // cost_type_ids). When the cost code changes and the current cost type
      // isn't valid for the new one, clear it — same UX as Sage 300 CRE. But
      // populate-safe: only enforce when cost_type_ids is actually synced
      // (non-empty); null/empty (not synced, or unknown) → keep the cost type
      // rather than clearing on data we can't trust.
      //
      // NOTE on the JC-vs-EM asymmetry (this keeps-on-empty, the EM cascade below
      // clears-on-[]): it's intentional and follows each backend's shape. The CDK
      // ingest stamps JC `cost_type_ids` ONLY when non-empty, so a JC cost code
      // with no valid types is `null` (→ keep), never `[]`. The EM ingest always
      // writes `emCostTypeCodes` (incl. `[]` for "no valid types"), so EM can and
      // does clear on `[]`. If JC ever starts emitting `[]`, switch this to the
      // EM's `Array.isArray` check.
      if (hasCostCodeScopedCostTypes && selectedCostCode && currentCostType) {
        const costCodeCostTypeIds =
          (selectedCostCode as unknown as { cost_type_ids?: string[] }).cost_type_ids || [];
        const stillValid =
          costCodeCostTypeIds.length === 0 || costCodeCostTypeIds.includes(currentCostType.id);
        if (!stillValid) {
          const costTypeName = currentCostType.name || currentCostType.code || 'selected cost type';
          const costCodeName = selectedCostCode.name || selectedCostCode.code || 'selected cost code';

          enqueueSnackbar(
            t('transactionSidesheet.costTypeCostCodeIncompatible', {
              costType: costTypeName,
              costCode: costCodeName,
              defaultValue: `${costTypeName} cannot be used with ${costCodeName}`,
            }),
            { variant: 'info', autoHideDuration: 3000 }
          );

          // Local state first (instant UI), then backend.
          dispatch(
            applyLocalTransactionUpdates([
              {
                id,
                changes: {
                  agaveCostCode: selectedCostCode || null,
                  agaveCostCodeId: selectedCostCode?.id || null,
                  agaveCostType: null,
                  agaveCostTypeId: null,
                },
              },
            ])
          );
          dispatch(
            updateTransactionAgaveClient({
              id,
              agaveCostCode: selectedCostCode?.id
                ? (removeTypename(selectedCostCode) as CreateAgaveCostCodeInput)
                : null,
              agaveCostType: null,
              // SEE-975: stamp the write seq so an out-of-order response can't
              // resurrect the cleared cost type (the flicker) — matches the
              // project/EM handlers.
              clientWriteSeq: nextAgaveCodingSeq(),
            })
          );
          return;
        }
      }

      // If cost code is being removed, also clear cost type (cost types require a cost code)
      if (!selectedCostCode && currentCostType) {
        const costTypeName = currentCostType.name || currentCostType.code || 'selected cost type';

        enqueueSnackbar(
          t('transactionSidesheet.costTypeRequiresCostCode', {
            costType: costTypeName,
            defaultValue: `${costTypeName} cleared - cost types require a cost code`,
          }),
          {
            variant: 'info',
            autoHideDuration: 3000,
          }
        );

        // CRITICAL: Update local state immediately for instant UI feedback
        dispatch(
          applyLocalTransactionUpdates([
            {
              id,
              changes: {
                agaveCostCode: null,
                agaveCostCodeId: null,
                agaveCostType: null,
                agaveCostTypeId: null,
              },
            },
          ])
        );

        // Then update backend asynchronously
        dispatch(
          updateTransactionAgaveClient({
            id,
            agaveCostCode: null,
            agaveCostType: null,
            // SEE-975: Vista/Spectrum write-seq guard (flicker fix); other ERPs keep
            // their existing no-seq behavior (this branch is shared).
            ...(hasCostCodeScopedCostTypes ? { clientWriteSeq: nextAgaveCodingSeq() } : {}),
          })
        );
        return;
      }

      // Normal cost code update (cost type is compatible or no cost type selected)
      // CRITICAL: Update local state immediately for instant UI feedback
      dispatch(
        applyLocalTransactionUpdates([
          {
            id,
            changes: {
              agaveCostCode: selectedCostCode || null,
              agaveCostCodeId: selectedCostCode?.id || null,
            },
          },
        ])
      );

      // Then update backend asynchronously
      dispatch(
        updateTransactionAgaveClient({
          id,
          agaveCostCode: selectedCostCode?.id
            ? (removeTypename(selectedCostCode) as CreateAgaveCostCodeInput)
            : null,
          // SEE-975: Vista/Spectrum write-seq guard (flicker fix); other ERPs keep
          // their existing no-seq behavior (this branch is shared).
          ...(hasCostCodeScopedCostTypes ? { clientWriteSeq: nextAgaveCodingSeq() } : {}),
        })
      );
    },
    [id, dispatch, enqueueSnackbar, t, agaveAccountInSourceSystem]
  );

  const handleChangeCostType = (selectedCostType: AgaveCostType | null) => {
    if (!id) return;

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[Agave] change CostType', { id, costTypeId: selectedCostType?.id });
    }

    // Update local state immediately for instant UI feedback
    dispatch(
      applyLocalTransactionUpdates([
        {
          id,
          changes: {
            agaveCostType: selectedCostType || null,
            agaveCostTypeId: selectedCostType?.id || null,
          },
        },
      ])
    );

    dispatch(
      updateTransactionAgaveClient({
        id,
        agaveCostType: selectedCostType?.id
          ? (removeTypename(selectedCostType) as CreateAgaveCostTypeInput)
          : null,
      })
    );

    // SEE-1389 — completing the (project, cost code, cost type) combo derives the
    // GL account from the AgaveCostCodeCostTypeGl join and auto-fills it,
    // OVERWRITING a previously selected GL (a cost-type change re-derives). Only
    // when the lookup returns exactly one GL — ambiguous/empty results leave the
    // GL for the user (the dropdown is already constrained to the valid set).
    // Mirrors triggerProjectDimensionAutoFill: staleness-guarded against rapid
    // re-coding, best-effort on failure.
    const txnForAutoFill = selectAllTransactions(store.getState()).find((t) => t.id === id);
    const paidolIdForAutoFill = txnForAutoFill?.paidolId;
    const projectIdForAutoFill = txnForAutoFill?.agaveProjectId;
    const costCodeIdForAutoFill = txnForAutoFill?.agaveCostCodeId;
    if (selectedCostType?.id && paidolIdForAutoFill && projectIdForAutoFill && costCodeIdForAutoFill) {
      dispatch(
        getGlCodesByCosting({
          paidolId: paidolIdForAutoFill,
          projectId: projectIdForAutoFill,
          costCodeId: costCodeIdForAutoFill,
          costTypeId: selectedCostType.id,
        })
      )
        .unwrap()
        .then(({ items, itemSourceId, itemName }) => {
          const txnNow = selectAllTransactions(store.getState()).find((t) => t.id === id);
          // Guard against the coding having moved on while the lookup ran.
          if (
            !txnNow ||
            txnNow.agaveCostTypeId !== selectedCostType.id ||
            txnNow.agaveProjectId !== projectIdForAutoFill ||
            txnNow.agaveCostCodeId !== costCodeIdForAutoFill
          ) {
            return;
          }

          const localChanges: Record<string, unknown> = {};
          const backendUpdates: Record<string, unknown> = {};

          // SEE-1389: exactly one valid GL for the combo -> auto-fill, overwriting
          // a previously selected GL. Ambiguous/empty -> leave for the user.
          if (items.length === 1 && txnNow.agaveGlCodeId !== items[0].id) {
            const cleaned = removeTypename(items[0]) as AgaveGlCode;
            localChanges.agaveGlCode = cleaned;
            localChanges.agaveGlCodeId = cleaned.id;
            backendUpdates.agaveGlCode = cleaned as CreateAgaveGlCodeInput;
          }

          // SEE-1390: the item FOLLOWS the cost type (AC: "item is auto populated
          // by the cost type selection"), mirroring the GL above — mapped cost
          // type sets it (and the billable indicator), a cost type with NO mapping
          // clears it (review: leaving a previously derived item + billable=true
          // coded against a cost type it no longer belongs to posted wrong). A
          // manual item pick holds until the next cost-type change. Only when the
          // paidol opted into billable posting.
          const billableEnabled = isBillablePostingEnabled(selectedPaidol);
          if (billableEnabled) {
            if (itemSourceId && txnNow.erpBillableItemId !== itemSourceId) {
              localChanges.erpBillableItemId = itemSourceId;
              localChanges.erpBillableItemName = itemName || itemSourceId;
              localChanges.erpBillable = true;
              backendUpdates.erpBillableItemId = itemSourceId;
              backendUpdates.erpBillableItemName = itemName || itemSourceId;
              backendUpdates.erpBillable = true;
            } else if (!itemSourceId && txnNow.erpBillableItemId) {
              localChanges.erpBillableItemId = null;
              localChanges.erpBillableItemName = null;
              localChanges.erpBillable = null;
              backendUpdates.erpBillableItemId = null;
              backendUpdates.erpBillableItemName = null;
              backendUpdates.erpBillable = null;
            }
          }

          if (Object.keys(localChanges).length === 0) return;
          dispatch(applyLocalTransactionUpdates([{ id, changes: localChanges }]));
          dispatch(
            updateTransactionAgaveClient({
              id,
              ...backendUpdates,
              clientWriteSeq: nextAgaveCodingSeq(),
            })
          );
        })
        .catch(() => {
          /* best-effort — failure leaves the GL as-is; the constrained dropdown still applies */
        });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChangeGlCostType = (_selectedGlCostType: AgaveGlCostType | null) => {
    return;
  };

  // SEE-974 — Vista equipment coding. Selecting equipment re-scopes the EMGroup,
  // so it clears the two EM selections (mirrors handleChangeProjects clearing its
  // dependents). The two EM dropdowns don't gate each other.
  //
  // Project and Equipment are mutually exclusive — coding equipment clears the
  // entire project group (project, sub job, cost code, cost type) so a row never
  // holds both, enforced here at coding time independent of the selected
  // Transaction type (e.g. on a GL/untyped row where neither group is greyed). It
  // does NOT change the transaction type: the type is the user's explicit choice
  // (only the Type dropdown changes it).
  const handleChangeEquipment = (selectedEquipment: AgaveEquipment | null) => {
    if (!id) return;

    const settingEquipment = !!selectedEquipment?.id;
    const projectGroupLocalClears: Partial<HighnoteTransactionEvent> = settingEquipment
      ? {
          agaveProject: null,
          agaveProjectId: null,
          agaveSubJob: null,
          agaveSubJobId: null,
          agaveCostCode: null,
          agaveCostCodeId: null,
          agaveCostType: null,
          agaveCostTypeId: null,
        }
      : {};

    dispatch(
      applyLocalTransactionUpdates([
        {
          id,
          changes: {
            agaveEquipment: selectedEquipment || null,
            agaveEquipmentId: selectedEquipment?.id || null,
            agaveEmCostCode: null,
            agaveEmCostCodeId: null,
            agaveEmCostType: null,
            agaveEmCostTypeId: null,
            ...projectGroupLocalClears,
          },
        },
      ])
    );

    dispatch(
      updateTransactionAgaveClient({
        id,
        agaveEquipment: selectedEquipment?.id ? (removeTypename(selectedEquipment) as AgaveEquipment) : null,
        agaveEmCostCode: null,
        agaveEmCostType: null,
        ...(settingEquipment && {
          agaveProject: null,
          agaveSubJob: null,
          agaveCostCode: null,
          agaveCostType: null,
        }),
        clientWriteSeq: nextAgaveCodingSeq(),
      })
    );
  };

  const handleChangeEmCostCode = (selectedEmCostCode: AgaveEmCostCode | null) => {
    if (!id) return;

    // SEE-975: EM cost types are cost-code-specific — the equipment-side mirror of
    // the JC cost-code cascade, and it reuses the SAME two snackbar keys so the two
    // flows read identically:
    //   - cost code REMOVED         -> clear cost type, "… cleared - cost types require a cost code"
    //   - cost code CHANGED to one whose synced `emCostTypeCodes` excludes the
    //     current type -> clear cost type, "… cannot be used with …"
    // Validity comes from the cost code's synced `emCostTypeCodes` (CODES).
    // Populate-safe by null only: an ARRAY (including `[]` — a cost code Vista has
    // synced with NO valid cost types) means "restrict to these", so `[]` clears;
    // only null/absent (never synced) is treated as unknown → keep the cost type.
    //
    // SEE-1325: this is deliberately still `emCostTypeCodes`, NOT the
    // AgaveEmCostCodeCostType join that now backs the option list — the
    // auto-clear needs the answer synchronously off the already-selected row,
    // and the join is server-side only. The two cannot drift (the ingest writes
    // both from the same Vista CostTypes array each sync), but it does mean
    // emCostTypeCodes is not dead: dropping it from the ingest or the
    // v2SearchAgaveEmCostCodes selection set breaks this guard silently.
    const currentEmCostType = selectAllTransactions(store.getState()).find(
      (tx) => tx.id === id
    )?.agaveEmCostType;
    const emCostTypeCodes = (selectedEmCostCode as unknown as { emCostTypeCodes?: (string | null)[] } | null)
      ?.emCostTypeCodes;
    const emCostCodeRemoved = !selectedEmCostCode && !!currentEmCostType;
    const emCostTypeIncompatible =
      !!selectedEmCostCode &&
      !!currentEmCostType &&
      Array.isArray(emCostTypeCodes) &&
      !emCostTypeCodes.includes(currentEmCostType.code ?? '');
    const shouldClearEmCostType = emCostCodeRemoved || emCostTypeIncompatible;

    if (shouldClearEmCostType && currentEmCostType) {
      const costTypeName = currentEmCostType.name || currentEmCostType.code || 'cost type';
      if (emCostCodeRemoved) {
        enqueueSnackbar(
          t('transactionSidesheet.costTypeRequiresCostCode', {
            costType: costTypeName,
            defaultValue: `${costTypeName} cleared - cost types require a cost code`,
          }),
          { variant: 'info', autoHideDuration: 3000 }
        );
      } else {
        const costCodeName = selectedEmCostCode?.name || selectedEmCostCode?.code || 'cost code';
        enqueueSnackbar(
          t('transactionSidesheet.costTypeCostCodeIncompatible', {
            costType: costTypeName,
            costCode: costCodeName,
            defaultValue: `${costTypeName} cannot be used with ${costCodeName}`,
          }),
          { variant: 'info', autoHideDuration: 3000 }
        );
      }
    }

    dispatch(
      applyLocalTransactionUpdates([
        {
          id,
          changes: {
            agaveEmCostCode: selectedEmCostCode || null,
            agaveEmCostCodeId: selectedEmCostCode?.id || null,
            ...(shouldClearEmCostType ? { agaveEmCostType: null, agaveEmCostTypeId: null } : {}),
          },
        },
      ])
    );

    dispatch(
      updateTransactionAgaveClient({
        id,
        agaveEmCostCode: selectedEmCostCode?.id
          ? (removeTypename(selectedEmCostCode) as AgaveEmCostCode)
          : null,
        ...(shouldClearEmCostType ? { agaveEmCostType: null } : {}),
        clientWriteSeq: nextAgaveCodingSeq(),
      })
    );
  };

  const handleChangeEmCostType = (selectedEmCostType: AgaveEmCostType | null) => {
    if (!id) return;

    dispatch(
      applyLocalTransactionUpdates([
        {
          id,
          changes: {
            agaveEmCostType: selectedEmCostType || null,
            agaveEmCostTypeId: selectedEmCostType?.id || null,
          },
        },
      ])
    );

    dispatch(
      updateTransactionAgaveClient({
        id,
        agaveEmCostType: selectedEmCostType?.id
          ? (removeTypename(selectedEmCostType) as AgaveEmCostType)
          : null,
        clientWriteSeq: nextAgaveCodingSeq(),
      })
    );
  };

  const handleChangeVendor = (selectedVendor: AgaveVendor | null) => {
    if (!id) return;

    if (selectedVendor?.id) {
      setSelectedVendorId((prev) => ({ ...prev, [id]: selectedVendor }));
    }

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[Agave] change Vendor', { id, vendorId: selectedVendor?.id });
    }

    dispatch(
      applyLocalTransactionUpdates([
        {
          id,
          changes: {
            agaveVendor: selectedVendor || null,
            agaveVendorId: selectedVendor?.id || null,
          },
        },
      ])
    );

    dispatch(
      updateTransactionAgaveClient({
        id,
        agaveVendor: selectedVendor?.id ? (removeTypename(selectedVendor) as CreateAgaveVendorInput) : null,
      })
    );
  };

  const handleChangeSubJob = useCallback(
    (selectedSubJob: AgaveSubJob | null) => {
      if (!id) return;

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[Agave] change SubJob', { id, subJobId: selectedSubJob?.id });
      }

      // Get the ABSOLUTE latest transaction from Redux store directly
      // This ensures we catch cost codes set milliseconds before, avoiding race conditions
      const currentState = store.getState();
      const allTransactionsNow = selectAllTransactions(currentState);
      const latestTransaction = allTransactionsNow.find((t) => t.id === id);
      const currentCostCode = latestTransaction?.agaveCostCode;
      const currentCostType = latestTransaction?.agaveCostType;

      // Check if we need to clear the cost code due to subjob change.
      // For Sage 300 CRE, cost codes are tied to specific project+subjob combos,
      // so ANY subjob change (add, remove, or switch) invalidates the current cost code.
      // For other ERPs, only clear if the new subjob's cost_code_ids exclude the current code.
      const isSage300CRE = agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_300_CRE);
      const currentSubJob = latestTransaction?.agaveSubJob;
      const subJobIsChanging = currentSubJob?.id !== selectedSubJob?.id;

      if (currentCostCode && subJobIsChanging) {
        let shouldClearForSubJob = false;
        let clearMessage = '';

        if (isSage300CRE) {
          // Sage 300 CRE: cost codes are per project+subjob, always clear on subjob change
          shouldClearForSubJob = true;
          const costCodeName = currentCostCode.name || currentCostCode.code || 'selected cost code';
          clearMessage = selectedSubJob
            ? t('transactionSidesheet.costCodeClearedForSubJobChange', {
                costCode: costCodeName,
                defaultValue: `${costCodeName} cleared - cost codes are sub-job-specific`,
              })
            : t('transactionSidesheet.costCodeClearedSubJobRemoved', {
                costCode: costCodeName,
                defaultValue: `${costCodeName} cleared - sub-job removed`,
              });
        } else if (selectedSubJob) {
          // Other ERPs: only clear if the new subjob explicitly excludes the cost code
          const subjobCostCodeIds = selectedSubJob.cost_code_ids || [];
          const currentCostCodeId = currentCostCode.id;
          if (subjobCostCodeIds.length > 0 && !subjobCostCodeIds.includes(currentCostCodeId)) {
            shouldClearForSubJob = true;
            const costCodeName = currentCostCode.name || currentCostCode.code || 'selected cost code';
            const subJobName = selectedSubJob.name || selectedSubJob.number || 'selected sub-job';
            clearMessage = t('transactionSidesheet.costCodeSubJobIncompatible', {
              costCode: costCodeName,
              subJob: subJobName,
              defaultValue: `${costCodeName} cannot be used with ${subJobName}`,
            });
          }
        }

        if (shouldClearForSubJob) {
          enqueueSnackbar(clearMessage, { variant: 'info', autoHideDuration: 3000 });

          // Clear the cost code AND cost type (since cost type depends on cost code)
          dispatch(
            applyLocalTransactionUpdates([
              {
                id,
                changes: {
                  agaveCostCode: null,
                  agaveCostCodeId: null,
                  agaveCostType: currentCostType ? null : undefined,
                  agaveCostTypeId: currentCostType ? null : undefined,
                  agaveSubJob: selectedSubJob?.id ? (removeTypename(selectedSubJob) as any) : null,
                  agaveSubJobId: selectedSubJob?.id || null,
                },
              },
            ])
          );
          dispatch(
            updateTransactionAgaveClient({
              id,
              agaveCostCode: null,
              agaveCostType: currentCostType ? null : undefined,
              agaveSubJob: selectedSubJob?.id ? (removeTypename(selectedSubJob) as any) : null,
            })
          );
          return;
        }
      }

      // Normal subjob update
      dispatch(
        applyLocalTransactionUpdates([
          {
            id,
            changes: {
              agaveSubJob: selectedSubJob?.id ? (removeTypename(selectedSubJob) as any) : null,
              agaveSubJobId: selectedSubJob?.id || null,
            },
          },
        ])
      );
      dispatch(
        updateTransactionAgaveClient({
          id,
          agaveSubJob: selectedSubJob?.id ? (removeTypename(selectedSubJob) as any) : null,
        })
      );
    },
    [id, dispatch, enqueueSnackbar, closeSnackbar, t, agaveAccountInSourceSystem]
  );
  const handleChangeLocation = useCallback(
    (selectedLocation: AgaveLocation | null) => {
      if (!id) return;

      // Read the freshest coding state from the store — the `transaction` prop
      // can be stale and doesn't reliably carry the flat agaveProjectId /
      // agaveLocationId, so gating on it silently no-oped. Mirror how
      // handleChangeProjects reads current values.
      const allTransactionsNow = selectAllTransactions(store.getState());
      const latestTransaction = allTransactionsNow.find((tx) => tx.id === id);
      const currentProjectId = latestTransaction?.agaveProject?.id;
      const currentLocationId = latestTransaction?.agaveLocation?.id;

      // SEE-944 — when the paidol narrows projects by location, changing or
      // clearing the location invalidates a project chosen under the previous
      // location. Clear the project AND its Sage Intacct dependents (cost code,
      // cost type, department) so they don't survive an invalidated project,
      // mirroring the project-removal cascade in handleChangeProjects.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const erpSetting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
      const shouldCascadeClearProject = Boolean(
        erpSetting?.isEnabled &&
          erpSetting?.locationEnabled &&
          erpSetting?.filterProjectsByEmployeeLocationEnabled &&
          currentProjectId &&
          selectedLocation?.id !== currentLocationId
      );

      const projectDependentLocalClears = shouldCascadeClearProject
        ? {
            agaveProject: null,
            agaveProjectId: null,
            agaveCostCode: null,
            agaveCostCodeId: null,
            agaveCostType: null,
            agaveCostTypeId: null,
            agaveDepartment: null,
            agaveDepartmentId: null,
          }
        : {};
      const projectDependentBackendClears = shouldCascadeClearProject
        ? {
            agaveProject: null,
            agaveCostCode: null,
            agaveCostType: null,
            agaveDepartment: null,
          }
        : {};

      // CRITICAL: Update local state immediately for instant UI feedback
      dispatch(
        applyLocalTransactionUpdates([
          {
            id,
            changes: {
              agaveLocation: selectedLocation?.id ? (removeTypename(selectedLocation) as any) : null,
              agaveLocationId: selectedLocation?.id || null,
              ...projectDependentLocalClears,
            },
          },
        ])
      );

      // Then update backend (async)
      dispatch(
        updateTransactionAgaveClient({
          id,
          agaveLocation: selectedLocation?.id ? (removeTypename(selectedLocation) as any) : null,
          ...projectDependentBackendClears,
          clientWriteSeq: nextAgaveCodingSeq(),
        })
      );
    },
    [id, dispatch, selectedPaidol]
  );

  const handleChangeDepartment = useCallback(
    (selectedDepartment: AgaveDepartment | null) => {
      if (!id) return;

      // CRITICAL: Update local state immediately for instant UI feedback
      dispatch(
        applyLocalTransactionUpdates([
          {
            id,
            changes: {
              agaveDepartment: selectedDepartment?.id ? (removeTypename(selectedDepartment) as any) : null,
              agaveDepartmentId: selectedDepartment?.id || null,
            },
          },
        ])
      );

      // Then update backend (async)
      dispatch(
        updateTransactionAgaveClient({
          id,
          agaveDepartment: selectedDepartment?.id ? (removeTypename(selectedDepartment) as any) : null,
        })
      );
    },
    [id, dispatch]
  );

  const handleChangeSubaccount = useCallback(
    (selectedSubaccount: AgaveSubaccount | null) => {
      if (!id) return;

      dispatch(
        applyLocalTransactionUpdates([
          {
            id,
            changes: {
              agaveSubaccount: selectedSubaccount?.id ? (removeTypename(selectedSubaccount) as any) : null,
              agaveSubaccountId: selectedSubaccount?.id || null,
            },
          },
        ])
      );

      dispatch(
        updateTransactionAgaveClient({
          id,
          agaveSubaccount: selectedSubaccount?.id ? (removeTypename(selectedSubaccount) as any) : null,
        })
      );
    },
    [id, dispatch]
  );

  // SEE-1135 — per-transaction billable item. Unlike the agave* fields this is a
  // flat id + denormalized name (not an entity object), and it has no dependents,
  // so no cascade-clear — just the optimistic local update + async persist.
  const handleChangeBillableItem = useCallback(
    (selectedItem: ErpPostingAccount | null) => {
      if (!id) return;

      const erpBillableItemId = selectedItem?.accountId || null;
      const erpBillableItemName = selectedItem?.accountId
        ? selectedItem.accountName ?? selectedItem.accountId
        : null;
      // SEE-1390: selecting an item auto-sets the billable indicator; clearing
      // the item clears it (null = legacy item-is-the-signal semantics).
      const erpBillable = selectedItem?.accountId ? true : null;

      dispatch(
        applyLocalTransactionUpdates([
          { id, changes: { erpBillableItemId, erpBillableItemName, erpBillable } },
        ])
      );

      dispatch(updateTransactionAgaveClient({ id, erpBillableItemId, erpBillableItemName, erpBillable }));
    },
    [id, dispatch]
  );

  // SEE-1390: per-transaction billable flip. Only the flag moves — the item
  // stays coded, and the post omits the billable line fields when false.
  const handleChangeBillable = useCallback(
    (billable: boolean) => {
      if (!id) return;
      dispatch(applyLocalTransactionUpdates([{ id, changes: { erpBillable: billable } }]));
      dispatch(updateTransactionAgaveClient({ id, erpBillable: billable }));
    },
    [id, dispatch]
  );

  const handleChangeEmployee = useCallback(
    (selectedEmployee: AgaveEmployeeEntity | null) => {
      if (!id) return;
      if (selectedEmployee == null) {
        registerEmployeeCleared?.(id);
      }

      // CRITICAL: Update local state immediately for instant UI feedback
      dispatch(
        applyLocalTransactionUpdates([
          {
            id,
            changes: {
              agaveEmployee: selectedEmployee?.id ? (removeTypename(selectedEmployee) as any) : null,
              agaveEmployeeId: selectedEmployee?.id || null,
            },
          },
        ])
      );

      // Then update backend (async)
      dispatch(
        updateTransactionAgaveClient({
          id,
          agaveEmployee: selectedEmployee?.id ? (removeTypename(selectedEmployee) as any) : null,
        })
      );

      // SEE-944 — when the paidol toggle is on, look up the employee's
      // mapped locations. If exactly one, auto-fill `agaveLocation` on the
      // transaction so the project dropdown immediately narrows. Multiple
      // locations → leave it for the user to pick from the (already
      // employee-narrowed) location dropdown. Fire-and-forget so the
      // handler signature stays sync for the dropdown callback contract.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const erpSetting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
      const shouldAutoFillLocation = Boolean(
        erpSetting?.isEnabled &&
          erpSetting?.locationEnabled &&
          erpSetting?.filterProjectsByEmployeeLocationEnabled &&
          selectedEmployee?.id &&
          selectedPaidol?.id
      );

      if (shouldAutoFillLocation && selectedEmployee && selectedPaidol?.id) {
        const employeeId = selectedEmployee.id;
        const paidolId = selectedPaidol.id;
        (async () => {
          try {
            const result = await agaveService.v2SearchAgaveLocations({
              input: {
                filter: { paidolId, employeeId },
                limit: 2,
              },
            });

            const items = (result?.items ?? []) as AgaveLocation[];
            if (items.length === 1) {
              const onlyLocation = items[0];
              // Mirror handleChangeLocation's cascade clear: if the row already
              // has a project under a different location, drop it so the
              // user's project dropdown refilters cleanly to the new location.
              const currentTxn = selectTransactionsSlice(store.getState()).entities[id];
              const shouldClearStaleProject = Boolean(
                currentTxn?.agaveProjectId && currentTxn?.agaveLocationId !== onlyLocation.id
              );
              dispatch(
                applyLocalTransactionUpdates([
                  {
                    id,
                    changes: {
                      agaveLocation: removeTypename(onlyLocation) as any,
                      agaveLocationId: onlyLocation.id,
                      ...(shouldClearStaleProject ? { agaveProject: null, agaveProjectId: null } : {}),
                    },
                  },
                ])
              );
              dispatch(
                updateTransactionAgaveClient({
                  id,
                  agaveLocation: removeTypename(onlyLocation) as any,
                  ...(shouldClearStaleProject ? { agaveProject: null } : {}),
                })
              );
            }
          } catch (e) {
            captureException(e);
          }
        })();
      }
    },
    [id, dispatch, registerEmployeeCleared, selectedPaidol]
  );

  const handleChangeAccountingDate = (accountingDate: string | null) => {
    if (!id) return;

    // Update backend
    dispatch(updateTransactionAgaveClient({ id, accountingDate }));
  };
  const createInitialSageIntacctVendor = () => {
    if (!agaveAccountToken) return;
    dispatch(
      createSageIntacctVendor({
        input: { account_token: agaveAccountToken, method: AgaveHttpMethod.POST, data: '' },
      })
    );
  };

  const handleMarkAgaveTransactionAsReviewed = () => {
    if (id) {
      return dispatch(markErpTransactionReviewed({ id }));
    }
  };

  const handlePostAgaveTransaction = async (transactionToPost?: HighnoteTransactionEvent) => {
    const txn = transactionToPost || transaction;
    if (!txn || !txn.id) return;

    // Prevent double-clicks: check if already posting and set posting state immediately
    if (postingTransactionIds.includes(txn.id)) {
      return; // Already posting, ignore this click
    }

    // Prevent re-posting: check if transaction is already posted or in progress (SEE-726)
    if (txn.erpPostingStatus === ERPPostingStatus.COMPLETED) {
      enqueueSnackbar(t('submitExpense.alreadyPosted'), { variant: 'info' });
      return;
    }
    if (
      txn.erpPostingStatus === ERPPostingStatus.PENDING ||
      txn.erpPostingStatus === ERPPostingStatus.PROCESSING
    ) {
      enqueueSnackbar(t('submitExpense.postingInProgress'), { variant: 'info' });
      return;
    }

    // Set posting state immediately to prevent double-clicks
    dispatch(setTransactionPosting({ id: txn.id, isPosting: true }));

    // Check if transaction is bypassed - bypassed transactions should not be posted
    if (txn.erpBypassStatus) {
      enqueueSnackbar(t('transactionBypassedCannotPost'), { variant: 'warning' });
      dispatch(setTransactionPosting({ id: txn.id, isPosting: false }));
      return;
    }

    // Spectrum / Vista on AppXchange: no Agave account needed — just flag for
    // AppXchange pickup. Vista's check is gated on erpSpeedchain so legacy
    // Vista paidols (no erpConnectionId) keep falling through to the Agave SDK
    // path below.
    const isAppXchangePosting =
      agaveAccountInSourceSystem(AgaveSourceSystem.SPECTRUM) ||
      (agaveAccountInSourceSystem(AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED) &&
        !!selectedPaidol?.erpSpeedchain);
    if (isAppXchangePosting) {
      try {
        await dispatch(
          updateTransaction({
            id: txn.id,
            erpApprovalStatus: true,
            erpPostingStatus: ERPPostingStatus.PENDING,
          })
        ).unwrap();
        enqueueSnackbar(t('submitExpense.spectrumQueued', 'Transaction queued for posting'), {
          variant: 'success',
        });
      } catch (error) {
        dispatch(refetchTransaction({ id: txn.id }));
        enqueueSnackbar(
          t('submitExpense.spectrumQueueUncertain', "Couldn't confirm queue status — refreshing transaction"),
          { variant: 'warning' }
        );
      } finally {
        dispatch(setTransactionPosting({ id: txn.id, isPosting: false }));
      }
      return;
    }

    if (!agaveAccountToken) {
      enqueueSnackbar(t('submitExpense.agaveAccountNotFound'), { variant: 'error' });
      dispatch(setTransactionPosting({ id: txn.id, isPosting: false }));
      return;
    }

    const {
      id,
      agaveCostCode,
      agaveCostType,
      agaveProject,
      agaveGlCode,
      agaveGlCodeId,
      agaveLocation,
      agaveDepartment,
      agaveLocationId,
      agaveDepartmentId,
      agaveCostCodeId,
      agaveCostTypeId,
      agaveExpenseId,
      agaveProjectId,
      agaveVendorId,
      agaveVendor,
      createdAt,
      eventDate,
      merchantName,
      note,
      status,
    } = txn;
    const cardDetail = cardDetails.get(txn.paymentCardId);
    const last4 = cardDetail?.last4 || txn.card?.last4;
    const costType = agaveCostType?.name;

    const projectCode = agaveProject?.source_id;
    const costCode = agaveCostCode?.source_id;

    const paymentNumber = id.substring(0, 15); // Use first 15 characters (max limit) to reduce collisions
    const paymentDate = new Date(eventDate ?? createdAt).toISOString().split('T')[0];

    const { amount } = formatAmountForAgavePost(txn.approvedAmount?.value ?? 0, status);

    const createExpense = (expense: CreateAgaveExpenseInput) => {
      setIsAgaveLoading(true);
      return dispatch(createOrUpdateAgaveExpense({ input: expense }))
        .unwrap()
        .then((expense) => {
          if (expense && 'id' in expense) {
            // Update transaction status immediately in frontend
            dispatch(
              updateTransaction({
                id,
                erpApprovalStatus: true,
                agaveExpense: removeTypename(expense),
              })
            );
            enqueueSnackbar(t('submitExpense.success'), { variant: 'success' });
          } else {
            enqueueSnackbar(mapAgaveErrorMessage((expense as AgaveError)?.message || 'Agave Error', t), {
              variant: 'error',
            });
          }
        })
        .catch((error) => {
          captureException(error);
          enqueueSnackbar(error.message || t('submitExpense.error'), { variant: 'error' });
        })
        .finally(() => {
          setIsAgaveLoading(false);
          dispatch(setTransactionPosting({ id: txn.id, isPosting: false }));
        });
    };

    const createAPInvoice = (apInvoice: AgaveAPInvoicesCreateExpenseInput) => {
      setIsAgaveLoading(true);
      return dispatch(createAgaveAPInvoice({ input: apInvoice }))
        .unwrap()
        .then((expense) => {
          if (expense && 'id' in expense) {
            // Update transaction status immediately in frontend
            dispatch(
              updateTransaction({
                id,
                erpApprovalStatus: true,
                agaveExpense: removeTypename(expense),
              })
            );
            enqueueSnackbar(t('submitExpense.successBill'), { variant: 'success' });
          } else {
            enqueueSnackbar(mapAgaveErrorMessage((expense as AgaveError)?.message || 'Agave Error', t), {
              variant: 'error',
            });
          }
        })
        .catch((error) => {
          captureException(error);
          enqueueSnackbar(error.message || t('submitExpense.error'), { variant: 'error' });
        })
        .finally(() => {
          setIsAgaveLoading(false);
          dispatch(setTransactionPosting({ id: txn.id, isPosting: false }));
        });
    };

    const qboHandlePostTransaction = async () => {
      // Check QBO posting settings: isEnabled = use bills, !isEnabled = use expenses
      const useBills = selectedPaidol?.qboPostingSettings?.isEnabled ?? false;

      const postingContext: PostingContext = {
        transaction: txn,
        dispatch,
        enqueueSnackbar,
        t,
        setIsAgaveLoading,
        setTransactionPosting,
        applyLocalTransactionUpdates,
        removeTypename,
        mapAgaveErrorMessage,
        captureException,
      };

      await postQBOTransaction(
        {
          transactionId: id,
          paidolId: selectedPaidol?.id || '',
          transaction: txn,
          agaveAccount,
          agaveAccountToken,
          useBills,
          qboUtil,
          createCreditCardCreditQBO,
          updateTransaction,
          removeTypename,
        },
        postingContext
      );
    };

    const sage300HandlePostTransaction = async () => {
      const postingContext: PostingContext = {
        transaction: txn,
        dispatch,
        enqueueSnackbar,
        t,
        setIsAgaveLoading,
        setTransactionPosting,
        applyLocalTransactionUpdates,
        removeTypename,
        mapAgaveErrorMessage,
        captureException,
      };

      await postSage300APInvoice(
        {
          transactionId: id,
          paidolId: selectedPaidol?.id || '',
          transaction: txn,
          agaveAccountToken,
        },
        postingContext
      );
    };

    const expense = {} as CreateAgaveExpenseInput;

    if (agaveExpenseId) expense.expense_id = txn.agaveExpenseId;

    if (!currentSourceSystem) {
      // Log for debugging - this should never happen but we need to know if it does
      captureException(new Error('Source system is not defined'), {
        tags: { component: 'AgaveTransactions' },
        extra: { agaveAccountId: agaveAccount?.account_id },
      });

      // Safe recovery - show user error and return
      enqueueSnackbar('Unable to determine ERP system', { variant: 'error' });
      dispatch(setTransactionPosting({ id: txn.id, isPosting: false }));
      return;
    }

    if (agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_100_CONTRACTOR, AgaveSourceSystem.CMIC)) {
      const apInvoice: AgaveAPInvoicesCreateExpenseInput = {
        description: txn.merchantName,
        merchantName: 'SPEEDCHAIN',
        id,
        account_token: agaveAccountToken,
        issue_date: paymentDate,
        line_items: [
          {
            amount,
            cost_code_id: agaveCostCodeId,
            cost_type_id: agaveCostTypeId,
            description: merchantName,
            ...(currentSourceSystem !== AgaveSourceSystem.SAGE_100_CONTRACTOR
              ? { project_id: agaveProjectId }
              : {}),
            ...(currentSourceSystem !== AgaveSourceSystem.CMIC && agaveGlCodeId
              ? { ledger_account_id: agaveGlCodeId }
              : {}),
            quantity: '1.0',
          },
        ],
        number: paymentNumber,
        vendor_id:
          agaveVendorId && vendors ? vendors.find((v) => v.id === agaveVendorId)?.source_id : agaveVendorId,
        ...(currentSourceSystem === AgaveSourceSystem.SAGE_100_CONTRACTOR && {
          status: 'Open',
          source_data: { InvoiceType: '1' },
          due_date: paymentDate,
        }),
        erp: currentSourceSystem,
      };
      return createAPInvoice(apInvoice);
    } else if (agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_300_CRE)) {
      // Check Sage 300 posting settings: isEnabled determines posting method
      // Toggle OFF (false) = Job Costs (legacy/default)
      // Toggle ON (true) = AP Invoices (new feature)
      const useAPInvoices = selectedPaidol?.sage300PostingSettings?.isEnabled ?? false;

      if (useAPInvoices) {
        // New feature: AP Invoice posting
        return sage300HandlePostTransaction();
      } else {
        // Legacy/default: Job Costs posting using async queue (prevents Lambda timeouts)
        const postingContext: PostingContext = {
          transaction: txn,
          dispatch,
          enqueueSnackbar,
          t,
          setIsAgaveLoading,
          setTransactionPosting,
          applyLocalTransactionUpdates,
          removeTypename,
          mapAgaveErrorMessage,
          captureException,
        };

        return postSage300JobCost(
          {
            transactionId: id,
            paidolId: selectedPaidol?.id || '',
            transaction: txn,
            agaveAccountToken,
            agaveGlCode,
          },
          postingContext
        );
      }
    } else if (agaveAccountInSourceSystem(AgaveSourceSystem.QUICKBOOKS_ONLINE)) {
      return qboHandlePostTransaction();
    } else if (agaveAccountInSourceSystem(AgaveSourceSystem.QUICKBOOKS_DESKTOP)) {
      const postingContext: PostingContext = {
        transaction: txn,
        dispatch,
        enqueueSnackbar,
        t,
        setIsAgaveLoading,
        setTransactionPosting,
        applyLocalTransactionUpdates,
        removeTypename,
        mapAgaveErrorMessage,
        captureException,
      };

      return postQBDTransaction(
        {
          transactionId: id,
          paidolId: selectedPaidol?.id || '',
          transaction: txn,
          agaveAccount,
          agaveAccountToken,
        },
        postingContext
      );
    } else if (agaveAccountInSourceSystem(AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED)) {
      const postingContext: PostingContext = {
        transaction: txn,
        dispatch,
        enqueueSnackbar,
        t,
        setIsAgaveLoading,
        setTransactionPosting,
        applyLocalTransactionUpdates,
        removeTypename,
        mapAgaveErrorMessage,
        captureException,
      };

      return postVistaTransaction(
        {
          transactionId: id,
          paidolId: selectedPaidol?.id || '',
          transaction: txn,
          agaveAccountToken,
        },
        postingContext
      );
    } else if (agaveAccountInSourceSystem(AgaveSourceSystem.SAGE_INTACCT)) {
      const postingContext: PostingContext = {
        transaction: txn,
        dispatch,
        enqueueSnackbar,
        t,
        setIsAgaveLoading,
        setTransactionPosting,
        applyLocalTransactionUpdates,
        removeTypename,
        mapAgaveErrorMessage,
        captureException,
      };

      return postSageIntacctTransaction(
        {
          transactionId: id,
          paidolId: selectedPaidol?.id || '',
          transaction: txn,
          agaveAccountToken,
          last4: last4 ?? undefined,
          intacctProjectsNotLicensed: !!selectedPaidol?.intacctProjectsNotLicensed,
        },
        postingContext
      );
    } else if (agaveAccountInSourceSystem(AgaveSourceSystem.ACUMATICA)) {
      const postingContext: PostingContext = {
        transaction: txn,
        dispatch,
        enqueueSnackbar,
        t,
        setIsAgaveLoading,
        setTransactionPosting,
        applyLocalTransactionUpdates,
        removeTypename,
        mapAgaveErrorMessage,
        captureException,
      };

      return postAcumaticaTransaction(
        {
          transactionId: id,
          paidolId: selectedPaidol?.id || '',
          transaction: txn,
          agaveAccountToken,
        },
        postingContext
      );
    }
  };

  // SEE-1042 — QuickBooks Desktop DIRECT posting has its own dispatch, separate
  // from handlePostAgaveTransaction, so it never runs the Agave-account guards or
  // source-system branches (a QBD-direct paidol has no Agave account/token). The
  // backend (startQBDPosting → qbd_posting) resolves the connection from the
  // QbwcConnection registry and ignores account_token, so we pass an empty token.
  const handlePostQbdDirectTransaction = async (transactionToPost?: HighnoteTransactionEvent) => {
    const txn = transactionToPost || transaction;
    if (!txn || !txn.id) return;
    // Guard the paidol id explicitly rather than sending "" to startQBDPosting
    // (matches handleConnect's guard in ERPConnectionPage).
    if (!selectedPaidol?.id) return;

    // Same double-click / already-posted guards as the Agave handler.
    if (postingTransactionIds.includes(txn.id)) return;
    if (txn.erpPostingStatus === ERPPostingStatus.COMPLETED) {
      enqueueSnackbar(t('submitExpense.alreadyPosted'), { variant: 'info' });
      return;
    }
    if (
      txn.erpPostingStatus === ERPPostingStatus.PENDING ||
      txn.erpPostingStatus === ERPPostingStatus.PROCESSING
    ) {
      enqueueSnackbar(t('submitExpense.postingInProgress'), { variant: 'info' });
      return;
    }
    dispatch(setTransactionPosting({ id: txn.id, isPosting: true }));
    if (txn.erpBypassStatus) {
      enqueueSnackbar(t('transactionBypassedCannotPost'), { variant: 'warning' });
      dispatch(setTransactionPosting({ id: txn.id, isPosting: false }));
      return;
    }

    const postingContext: PostingContext = {
      transaction: txn,
      dispatch,
      enqueueSnackbar,
      t,
      setIsAgaveLoading,
      setTransactionPosting,
      applyLocalTransactionUpdates,
      removeTypename,
      mapAgaveErrorMessage,
      captureException,
    };

    return postQBDTransaction(
      {
        transactionId: txn.id,
        paidolId: selectedPaidol?.id || '',
        transaction: txn,
        agaveAccount: undefined,
        agaveAccountToken: '',
      },
      postingContext
    );
  };

  return {
    agaveAccountInSourceSystem,
    createInitialSageIntacctVendor,
    extractFullNameFromSourceData,
    extractFullyQualifiedNameFromSourceData,
    isERPSupportingGLCodes,
    isQuickbooksAgaveClient,
    handleChangeGlCodes,
    handleChangeProjects,
    handleChangeCostCode,
    handleChangeCostType,
    handleChangeGlCostType,
    handleChangeEquipment,
    handleChangeEmCostCode,
    handleChangeEmCostType,
    handleChangeVendor,
    handleChangeSubJob,
    handleChangeLocation,
    handleChangeDepartment,
    handleChangeSubaccount,
    handleChangeEmployee,
    handleChangeBillableItem,
    handleChangeBillable,
    handleChangeAccountingDate,
    handleMarkAgaveTransactionAsReviewed,
    handlePostAgaveTransaction,
    handlePostQbdDirectTransaction,
    selectedVendor,
    isAgaveLoading,
    mapAgaveErrorMessage,
    isAgaveTransactionAllowedToCode: isAgaveTransactionAllowedToCode(transaction, currentSourceSystem),
  };
};
